import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import ErrorMessage from '@components/common/alerts/ErrorMessage';
import UniversalInput from '@lib/wrappers/UniversalInput';

// Constants

import { THC_TYPES } from '@constants/ssap/hhmTypes/taxRelationsTypes';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';

// Utils

import { isEqual } from '@utils/miscellaneous/objectHelpers';



const DependentsBlock = props => {
    const {
        analyzeFamily,
        family,
        itfWarnings,
        onFieldChange,
        ...restProps
    } = props;

    const getDependents = () => {

        return family.combinedFilers
            .filter(filer => filer.isFilingTaxes)
            .map(filer => {

                const {
                    filerPersonId,
                    filerFullName
                } = filer;

                let adjustedFilerFullName = filerFullName;

                const getCombinedNames = names => {
                    const [ text1, text2 ] = names;
                    return (
                        <FormattedMessage
                            id = 'common.non_internationalized_text1_and_text2'
                            values = {{ text1, text2 }}
                        />
                    );
                };

                if(Array.isArray(filerFullName)) {
                    adjustedFilerFullName = getCombinedNames(filerFullName);
                }

                const checkDependent = dependent => {
                    const {
                        dependentPersonId,
                        spouse
                    } = dependent;

                    if(spouse && spouse[0] === filerPersonId) {
                        return false;
                    }

                    if(Array.isArray(filerPersonId)) {
                        return filerPersonId.every(id => id !== dependentPersonId);
                    } else {
                        return dependentPersonId !== filerPersonId;
                    }
                };

                let dependents = family.dependents.filter(dependent => checkDependent(dependent));

                return (
                    dependents.length && 
                    <Fragment key={filerPersonId}>
                        <fieldset>
                            <legend data-testid='dependents_of_legend'>
                                <FormattedMessage
                                    id='ssap.family_household.tax_composition.dependents_of'
                                    values={{
                                        filerFullName: <strong>{adjustedFilerFullName}</strong>
                                    }}
                                />
                            </legend>
                            {
                                dependents
                                    .map(dependent => {

                                        const {
                                            claimerIds,
                                            dependentIndex,
                                            dependentPersonId,
                                            dependentFullName,
                                            isDependent,
                                            isAbleToBeDependent
                                        } = dependent;

                                        const isChecked = () => {
                                            if(Array.isArray(filerPersonId)) {
                                                return filerPersonId.some(id => claimerIds.find(claimerId => claimerId === id));
                                            } else {
                                                return claimerIds.some(claimerId => claimerId === filerPersonId);
                                            }
                                        };

                                        const isFilerClaimed = (() => Array.isArray(filerPersonId) ? false : analyzeFamily.getIndicatorsValues(filerPersonId).taxRelationship === THC_TYPES.FILER_DEPENDENT)();

                                        const isDisabled = () => {
                                            if(isFilerClaimed) {
                                                return true;
                                            }

                                            if(Array.isArray(filerPersonId)) {
                                                return !isAbleToBeDependent && !filerPersonId.some(id => claimerIds.find(claimerId => claimerId === id));
                                            } else {
                                                return !isAbleToBeDependent && !claimerIds.some(claimerId => claimerId === filerPersonId);
                                            }
                                        };

                                        const itfName = itfWarnings[dependentIndex].itfFullName;

                                        const isITFWarningShown = itfWarnings[dependentIndex].isWarningShown
                                        && isEqual(itfName, filerFullName);

                                        return (
                                            <Fragment key={dependentPersonId}>
                                                <UniversalInput
                                                    label={{
                                                        text: 'common.input.label.non_internationalized_text',
                                                        labelTextReplacements: {
                                                            text: dependentFullName
                                                        }
                                                    }}
                                                    inputData={{
                                                        isInputRequired: false,
                                                        type: INPUT_TYPES.CHECKBOX,
                                                        name: 'isDependent',
                                                        classes: [''],
                                                        checked: isChecked(),
                                                        value: isDependent,
                                                        currentData: { },
                                                        disabled: isDisabled(),
                                                        fields: [],
                                                    }}
                                                    inputActions={{
                                                        onFieldChange
                                                    }}
                                                    indexInArray={[dependentIndex, filerPersonId]}
                                                    {...restProps}
                                                />
                                                {
                                                    isITFWarningShown &&

                                                <ErrorMessage
                                                    { ...{
                                                        errorMessage: 'ssap.validation_error_message.itf_dependent',
                                                        errorMessageReplacements: {
                                                            itfName: Array.isArray(itfName)
                                                                ? getCombinedNames(itfName)
                                                                : itfName
                                                        },
                                                        isCrossButtonHidden: true
                                                    } }
                                                />
                                                }
                                            </Fragment>
                                        );
                                    })
                            }
                        </fieldset>
                    </Fragment>
                );
            }).filter(f => f);
    };

    const preparedDependents = getDependents();

    const isSomethingForRender =
        family.pairs
            .every(pair => pair.isAnyOfSpousesFiler ? pair.isPairMadeChoice : true) &&
        family.filersQty > 0 &&
        family.possibleDependents > 0 &&
        preparedDependents.length;

    return  isSomethingForRender ?

        <div className="subsection">
            <FormattedMessage id='ssap.family_household.tax_composition.who_dependents' tagName='p'/>
            {
                preparedDependents
            }
        </div> : null;
};

export default DependentsBlock;